// Die Sprechblasen (task-016, docs/architektur-paket-2.md, Abschnitt „Client“): Wer einen der festen
// Sätze gesagt hat, trägt ihn vier Sekunden über dem Kopf – über dem Namensschild. Auch der eigene
// Satz steht so über der eigenen Figur; zu sehen ist er dort in der Schulterkamera.
//
// Wer was sagt und wie lange noch, ist reine Logik (`createSprechblasen`); die Blase selbst ist ein
// Sprite mit Canvas-Textur, wie das Namensschild (render/signs.ts).
import { CanvasTexture, LinearFilter, SRGBColorSpace, Sprite, SpriteMaterial } from 'three';
import { NAMENSSCHILD } from './signs.js';

/** So lange steht ein Satz über dem Kopf. */
export const SPRECHBLASE_DAUER_MS = 4000;

/** Lage und Größe der Blase in Blöcken; `hoehe` ist die Mitte über dem Fußpunkt. */
export const SPRECHBLASE = {
  tafelHoehe: 0.7,
  breite: 1.6,
  hoehe: NAMENSSCHILD.hoehe + NAMENSSCHILD.tafelHoehe / 2 + 0.1 + 0.7 / 2,
} as const;

/** Wer gerade was sagt; die Zeiten sind Eingangszeiten in Millisekunden. */
export interface Sprechblasen {
  /** Ein Satz kam an; er ersetzt den vorigen desselben Spielers. */
  sage(id: number, text: string, jetzt: number): void;
  /** Der Satz, der zur Zeit `jetzt` über dem Spieler steht, oder null. */
  text(id: number, jetzt: number): string | null;
  /** Der Spieler ist gegangen: Seine Blase verschwindet sofort. */
  vergiss(id: number): void;
  /** Alle Blasen weg, etwa nach einer neuen Begrüßung. */
  vergissAlle(): void;
}

export function createSprechblasen(): Sprechblasen {
  const saetze = new Map<number, { text: string; bis: number }>();
  return {
    sage(id, text, jetzt) {
      saetze.set(id, { text, bis: jetzt + SPRECHBLASE_DAUER_MS });
    },
    text(id, jetzt) {
      const satz = saetze.get(id);
      return satz !== undefined && jetzt < satz.bis ? satz.text : null;
    },
    vergiss(id) {
      saetze.delete(id);
    },
    vergissAlle() {
      saetze.clear();
    },
  };
}

/** Auflösung der Malfläche; das Seitenverhältnis ist das der Blase. */
const MALFLAECHE = { breite: 256, hoehe: 112 } as const;
/** Höhe des Zipfels unten, in Pixeln der Malfläche. */
const ZIPFEL = 22;
const FARBEN = { grund: '#fffaf0', rand: '#3a2412', schrift: '#2b1d0e' } as const;

export interface Sprechblase {
  /** Das Sprite; der Aufrufer hängt es an die Figur. */
  object: Sprite;
  /** Der Satz, oder null: dann ist die Blase unsichtbar. */
  setText(text: string | null): void;
  /** Gibt Textur und Material frei; aus der Szene nimmt sie die Figur mit. */
  dispose(): void;
}

export function createSprechblase(): Sprechblase {
  const canvas = document.createElement('canvas');
  canvas.width = MALFLAECHE.breite;
  canvas.height = MALFLAECHE.hoehe;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Die Sprechblase braucht einen 2d-Kontext; der Browser gab keinen her.');

  const texture = new CanvasTexture(canvas);
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.colorSpace = SRGBColorSpace;
  const material = new SpriteMaterial({ map: texture, transparent: true, depthWrite: false });
  const object = new Sprite(material);
  object.name = 'sprechblase';
  object.scale.set(SPRECHBLASE.breite, SPRECHBLASE.tafelHoehe, 1);
  object.position.set(0, SPRECHBLASE.hoehe, 0);
  object.visible = false;

  let gemalt: string | null = null;
  return {
    object,
    setText(text) {
      object.visible = text !== null;
      if (text === null || text === gemalt) return;
      gemalt = text;
      malSprechblase(ctx, text);
      texture.needsUpdate = true;
    },
    dispose() {
      texture.dispose();
      material.dispose();
    },
  };
}

/** Malt eine helle, runde Blase so breit wie der Satz, mit einem Zipfel nach unten zum Kopf. */
function malSprechblase(ctx: CanvasRenderingContext2D, text: string): void {
  const { breite: b, hoehe: h } = MALFLAECHE;
  const koerper = h - ZIPFEL - 4;
  ctx.clearRect(0, 0, b, h);
  ctx.font = 'bold 36px system-ui, sans-serif';
  const breite = Math.min(b - 8, ctx.measureText(text).width + 40);

  ctx.beginPath();
  ctx.roundRect((b - breite) / 2, 4, breite, koerper - 4, 20);
  ctx.fillStyle = FARBEN.grund;
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = FARBEN.rand;
  ctx.stroke();

  // Der Zipfel deckt den Rand der Blase an seiner Wurzel, darum erst füllen, dann nur seine Seiten ziehen.
  ctx.beginPath();
  ctx.moveTo(b / 2 - 14, koerper - 2);
  ctx.lineTo(b / 2, h - 4);
  ctx.lineTo(b / 2 + 14, koerper - 2);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(b / 2 - 14, koerper);
  ctx.lineTo(b / 2, h - 4);
  ctx.lineTo(b / 2 + 14, koerper);
  ctx.stroke();

  ctx.fillStyle = FARBEN.schrift;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, b / 2, koerper / 2 + 3, b - 24);
}
